<template>
    <div id="search">
        <USPBanner v-if="uspBannerVisible" />

        <Teaser :backgroundUrl="teaserBackground" :gift-box-banner-visible="searchResult.giftBoxBannerVisible" />

        <div class="search-content container main">
            <SearchBar :filterOptions="filterOptions" v-if="isSearchVisible" />

            <template v-if="initialState">
                <SectionHeader class="mt-3 mb-1">
                    {{ $t('Search.header.initial') }}
                </SectionHeader>
                <p class="search-text-initial">
                    {{ $t('Search.text.initial', { buttonText: $t('SearchBar.button.search.text') }) }}
                </p>
            </template>

            <template v-else-if="products">
                <Grid :grid="searchResultProducts" first>
                    <select v-model="sortDirection" class="form-control input-gray sort-order custom-select">
                        <option v-for="sort in sortDirections" :key="sort.value" :value="sort.value">
                            {{ sort.text }}
                        </option>
                    </select>

                    <template v-slot:loadMore>
                        <div class="swiper-slide">
                            <GridLoadMore :in-progress="loadingMore" @loadMore="loadMoreResults" />
                        </div>
                    </template>
                </Grid>
            </template>

            <template v-else>
                <SectionHeader class="mt-3 mb-1">
                    {{ $t('Search.header.noResults') }}
                </SectionHeader>
                <p>
                    {{ $t('Search.text.noResults') }}
                </p>
            </template>

            <PageContent
                v-if="$getSafe(searchResult, 'pageData.content')"
                :content="$getSafe(searchResult, 'pageData.content')"
                :small="!!$getSafe(searchResult, 'grids.length')"
            >
            </PageContent>

            <Grid v-if="relatedProducts.length" :grid="relatedProducts" :first="!searchResult.products.length" />
        </div>
    </div>
</template>

<script>
import getHead from '@/mixins/getHead';
import SearchBar from '@/components/search/SearchBar';
import Grid from '@/components/grid/Grid';
import Teaser from '@/components/teasers/Teaser';
import PageContent from '@/components/PageContent';
import GridModel from '@/models/Grid';
import searchable from '@/mixins/searchable';
import SectionHeader from '@/components/SectionHeader';
import GridLoadMore from '@/components/grid/GridLoadMore';
import advertisable from '@/mixins/advertisable';
import fetchResource from '@/utils/fetchResource';

export default {
    name: 'SearchResult',
    layout: ({ layout }) => layout,
    mixins: [getHead, searchable, advertisable('searchResult')],
    head() {
        return this.getHead({
            pageTitle: this.title,
            noindex: true
        });
    },
    components: {
        Grid,
        Teaser,
        PageContent,
        SearchBar,
        SectionHeader,
        GridLoadMore
    },
    async fetch(context) {
        const { store, query } = context;
        const filterOptions =
            typeof query.filterOptions === 'string' ? [query.filterOptions] : query.filterOptions || [];

        await fetchResource(
            context,
            store.dispatch('postSearch', {
                params: {
                    query: query.q,
                    tags: query.tags ? query.tags.split(',') : [],
                    page: query.page || 1,
                    pageSize: query.pageSize || 20,
                    filterOptions,
                    sortDirection: query.sort,
                    arrivalDateRange: query.from ? {
                      from: query.from,
                      until: query.until
                    } : null
                },
                mergeResults: false
            })
        );
    },
    watchQuery: true,
    data() {
        const sortDirections = [
            { text: this.$t('Search.sort.relevance'), value: null },
            { text: this.$t('Search.sort.priceAsc'), value: 'asc' },
            { text: this.$t('Search.sort.priceDesc'), value: 'desc' }
        ];
        return {
            defaultPage: 1,
            defaultPageSize: 20,
            defaultSort: sortDirections[0].value,
            sortDirections,
            loadingMore: false
        };
    },
    computed: {
        products() {
            return this.searchResult.products || [];
        },
        title() {
            return this.searchResult.query
                ? this.$t('Search.header', { query: this.searchResult.query })
                : this.$t('Search.header.noTerm');
        },
        isSearchVisible() {
            return this.$store.state.globalMeta.searchBarVisible;
        },
        searchResultProducts() {
            return new GridModel(
                this.title,
                this.products,
                null,
                3,
                this.searchResult.page,
                this.searchResult.numberOfPages
            );
        },
        relatedProducts() {
            return new GridModel(this.$t('ProductDetails.relatedOffers.header'), this.searchResult.relatedItems);
        },
        // filterOptions() {
        //     return (typeof this.$route.query.filterOptions === 'string'
        //         ? [this.$route.query.filterOptions]
        //         : this.$route.query.filterOptions) || [];
        // },
        pages() {
            const { page, numberOfPages } = this.searchResult;
            if (numberOfPages >= 3) {
                if (page === 1) {
                    return [1, 2, 3];
                } else if (page === numberOfPages) {
                    return [page - 2, page - 1, page];
                } else return [page - 1, page, page + 1];
            } else return Array.from({ length: numberOfPages }, (v, k) => k + 1);
        },
        page: {
            get() {
                return this.$route.query.page || this.defaultPage;
            },
            set(page) {
                this.updateQuery({ page });
            }
        },
        pageSize: {
            get() {
                return this.$route.query.pageSize || this.defaultPageSize;
            },
            set(pageSize) {
                this.updateQuery({ pageSize });
            }
        },
        sortDirection: {
            get() {
                return this.$route.query.sort || this.defaultSort;
            },
            set(sort) {
                this.updateQuery({ sort });
            }
        }
    },
    methods: {
        updateQuery(param = {}) {
            this.$router.push({
                query: {
                    ...this.$route.query,
                    ...param
                },
                hash: '#searchBar'
            });
        },
        async loadMoreResults() {
            this.loadingMore = true;

            try {
                const queryParams = {
                    filterOptions: this.$route.query.filterOptions,
                    query: this.$route.query.q,
                    arrivalDateRange: this.$route.query.from ? {
                        from: this.$route.query.from,
                        until: this.$route.query.until
                    } : null
                };
                await this.$store.dispatch('postSearch', {
                    params: {
                        ...queryParams,
                        pageSize: this.searchResult.pageSize,
                        page: this.searchResult.page + 1,
                        sortDirection: this.sortDirection
                    },
                    mergeResults: true
                });
            } catch (e) {
            } finally {
                this.loadingMore = false;
            }
        },
        sendGTMEvent() {
            this.$gtm && this.$gtm.pushPageView('mapSearch');
        }
    },
    beforeMount() {
        this.sendGTMEvent();
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';

#search {
    .search-text-initial {
        background-color: var(--body-bg);
    }

    .search-content {
        min-height: 500px;

        .sort-order {
            float: right;
            width: auto;
            height: 2em;
            margin-left: variables.$grid-padding;
            padding: 0 variables.$grid-padding;
            padding-right: 30px;
            line-height: 1;
        }
    }
}
</style>
